import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  FaInstagram,
  FaFacebookF,
  FaExternalLinkAlt,
  FaStar,
  //FaCalendarAlt,
} from "react-icons/fa";

const TutorSocialConnect = () => {
  const location = useLocation();

  // Determine activeTab based on current route
  const activeTab = location.pathname.startsWith('/tutor')
    ? 'tutor'
    : 'student';

  useEffect(() => {
    // Add animation to social cards on page load
    const socialCards = document.querySelectorAll(".social-card");

    socialCards.forEach((card, index) => {
      card.style.transitionDelay = `${index * 0.1}s`;
      card.classList.add("opacity-0", "translate-y-5");

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove("opacity-0", "translate-y-5");
          }
        });
      });

      observer.observe(card);
    });

    // Add glowing effect to CTA button
    const ctaButton = document.querySelector(".cta-button");
    if (ctaButton) {
      const interval = setInterval(() => {
        ctaButton.classList.toggle("ring-2");
        ctaButton.classList.toggle("ring-blue-400");
      }, 2000);

      return () => clearInterval(interval);
    }
  }, []);

  return (
    <div className="my-[100px] flex items-center justify-center p-4">
      <div className="max-w-5xl w-full mx-auto">
        <div className="bg-white rounded-xl     overflow-hidden">
          {/* Main Content */}
          <div className="p-6 md:p-8">
            <div className="text-center mb-8">
              <h3 className="text-4xl font-semibold text-gray-800">
                {activeTab === "student"
                  ? "Join Tutor Kendra's Student Community"
                  : "Join Tutor Kendra's Tutor Community"}
              </h3>
              <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
                {activeTab === "student"
                  ? "Get daily CBSE/ICSE study tips, exam strategies, and success stories to excel in your studies."
                  : "Connect with students, share your expertise, and grow as a tutor with our vibrant community."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Instagram Card */}
              <div className="social-card social-pulse bg-gradient-to-br from-pink-500 to-purple-600 text-white rounded-xl p-6 transition duration-300 cursor-pointer">
                <div className="flex items-center mb-4">
                  <div className="social-icon bg-white text-pink-500 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <FaInstagram className="text-2xl" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Instagram</h4>
                    <p className="text-sm opacity-90">@tutorkendra</p>
                  </div>
                </div>
                <div className="mb-4">
                  <p className="opacity-90">
                    {activeTab === "student"
                      ? "Follow for CBSE/ICSE study hacks, quick revision notes, and student success stories."
                      : "Share teaching tips, connect with students, and showcase your tutoring journey."}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-xs">
                    #CBSE
                  </span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-xs">
                    #ICSE
                  </span>
                  <span className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-xs">
                    {activeTab === "student" ? "#JEEPreparation" : "#Teaching"}
                  </span>
                </div>
                <button
                  onClick={() =>
                    window.open(
                      "https://www.instagram.com/tutorkendra?igsh=eTIwYXA4aWpsdzg3",
                      "_blank"
                    )
                  }
                  className="w-full bg-white text-pink-600 hover:bg-opacity-90 py-2 rounded-lg font-medium flex items-center justify-center mt-2 transition duration-300"
                >
                  Follow Us <FaExternalLinkAlt className="ml-2 text-sm" />
                </button>
              </div>

              {/* Facebook Card */}
              <div className="social-card social-pulse bg-gradient-to-br from-blue-600 to-blue-800 text-white rounded-xl p-6 transition duration-300 cursor-pointer">
                <div className="flex items-center mb-4">
                  <div className="social-icon bg-white text-blue-600 rounded-full w-12 h-12 flex items-center justify-center mr-4">
                    <FaFacebookF className="text-2xl" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Facebook</h4>
                    <p className="text-sm opacity-90">Tutor Kendra</p>
                  </div>
                </div>
                <div className="mb-4">
                  <p className="opacity-90">
                    {activeTab === "student"
                      ? "Join for live doubt-solving sessions, exam tips, and scholarship opportunities."
                      : "Engage with students, participate in live sessions, and build your tutoring network."}
                  </p>
                </div>
                <div className="flex items-center text-sm mb-4">
                  <div className="mr-6">
                    <div className="font-bold">5K+</div>
                    <div className="opacity-80 text-xs">
                      {activeTab === "student" ? "Students" : "Tutors"}
                    </div>
                  </div>
                  <div className="mr-6">
                    <div className="font-bold">200+</div>
                    <div className="opacity-80 text-xs">Expert Tutors</div>
                  </div>
                  <div>
                    <div className="font-bold">
                      4.9 <FaStar className="text-yellow-300 inline" />
                    </div>
                    <div className="opacity-80 text-xs">Rating</div>
                  </div>
                </div>
                <button
                  onClick={() =>
                    window.open(
                      "https://www.facebook.com/people/Tutor-Kendra/61577973922898/",
                      "_blank"
                    )
                  }
                  className="w-full bg-white text-blue-600 hover:bg-opacity-90 py-2 rounded-lg font-medium flex items-center justify-center mt-2 transition duration-300"
                >
                  Follow Page <FaExternalLinkAlt className="ml-2 text-sm" />
                </button>
              </div>
            </div>
          </div>

          {/* Footer CTA */}
          {/* <div className=" p-6 text-center border-t border-gray-200">
            <button className="cta-button bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-full font-medium transition duration-300 transform hover:scale-105">
              Book a Free Demo Class <FaCalendarAlt className="ml-2 inline" />
            </button>
            <p className="text-gray-500 text-sm mt-3">
              Follow us for special discounts and free resources!
            </p>
          </div> */}
        </div>
      </div>

      <style jsx>{`
        .social-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
        }
        .social-pulse {
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(59, 130, 246, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
          }
        }
        .social-icon {
          transition: all 0.3s ease;
        }
        .social-card:hover .social-icon {
          transform: scale(1.1);
        }
      `}</style>
    </div>
  );
};

export default TutorSocialConnect;